import {
  PhoneInput,
  defaultCountries,
  parseCountry,
  buildCountryData,
} from 'react-international-phone'
import type { CountryData } from 'react-international-phone'
import 'react-international-phone/style.css'
import { Input } from '@mantine/core'

/**
 * Correções de máscara por país. A lib limita o comprimento da entrada ao
 * tamanho da máscara ativa — uma máscara curta trunca o número digitado.
 *
 * Argentina: o celular internacional é +54 9 11 XXXX-XXXX = 11 dígitos após o
 * DDI (o "9" de móvel + 2 dígitos de área de Buenos Aires + 8 do assinante),
 * mas a máscara padrão da lib para `/^9/` só cabe 10 dígitos, cortando 1.
 * Ampliamos a máscara `/^9/` para 11 posições. As demais máscaras da AR são
 * mantidas como estão.
 */
const MASK_FIXES: Record<string, Record<string, string>> = {
  ar: {
    // 1 (móvel) + 2 (área) + 4 + 4 = 11 → cabe +54 9 11 1234 5678
    '/^9/': '(.) .. .... ....',
  },
}

/**
 * Lista de países com as máscaras corrigidas. Calculada uma única vez em
 * module scope para não recomputar a cada render.
 */
export const countries: CountryData[] = defaultCountries.map((c) => {
  const parsed = parseCountry(c)
  const fix = MASK_FIXES[parsed.iso2]
  if (!fix) return c
  // `format` pode ser string OU objeto com chaves regex-like (`FormatConfig`,
  // que exige a chave `default`). Os países em MASK_FIXES têm `format` como
  // objeto (com `default`), então preservamos as máscaras existentes e
  // sobrescrevemos apenas as corrigidas. Se, por algum motivo, o país deixar
  // de ter formato-objeto na lib, mantemos o país intacto (não aplicamos fix).
  if (typeof parsed.format !== 'object' || parsed.format === null) return c
  return buildCountryData({
    ...parsed,
    format: { ...parsed.format, ...fix },
  })
})

interface PhoneInputIntlProps {
  label?: string
  value: string
  onChange: (e164: string) => void
  required?: boolean
  error?: string
  /** País inicial (ISO2 minúsculo). Padrão: `'br'` (Brasil). */
  defaultCountry?: string
}

/**
 * Telefone internacional (D-UX2): seletor de país por bandeira + DDI
 * automático. Armazena E.164 (ex.: +5511912345678, +5491112345678).
 *
 * Cada país usa sua própria máscara (fornecida pela lib), com correções
 * pontuais em `MASK_FIXES` para máscaras que truncavam o número (Argentina).
 * O padrão continua sendo Brasil (`defaultCountry='br'`), mas o componente é
 * genérico e aceita qualquer país inicial.
 */
export function PhoneInputIntl({
  label,
  value,
  onChange,
  required,
  error,
  defaultCountry = 'br',
}: PhoneInputIntlProps) {
  return (
    <Input.Wrapper label={label} required={required} error={error}>
      <PhoneInput
        defaultCountry={defaultCountry}
        countries={countries}
        value={value}
        onChange={(phone) => onChange(phone)}
        placeholder={defaultCountry === 'br' ? '(11) 91234-5678' : undefined}
        inputProps={{ 'aria-label': label ?? 'Telefone' }}
      />
    </Input.Wrapper>
  )
}
